import { Provider } from '@angular/core';
import { OPERATORS } from 'src/app/services/constants';
import {
  SensorToolbarConfig,
  SENSOR_TOOLBAR_CONFIGS,
} from '../shared/contracts/toolbar-control.tokens';

/**
 * AISSTREAM'S TOOLBAR COMPOSITION — which controls this sensor shows, in which
 * slot, in what order (D13).
 *
 * Split out of `shared/toolbar/sensor-toolbar.configs.ts` alongside Voyage
 * Radar's; see `voyage-radar/toolbar/vr-toolbar.config.ts` for why.
 *
 * This provider registers NO control descriptors of its own — every control it
 * mounts is one of the sixteen provider-neutral ones in the shared registry,
 * so there is no `aisstream-toolbar-controls.providers.ts` beside this file.
 * That asymmetry is the architecture working rather than an omission: a
 * control is shared when its state and its action both live on
 * `PlaybackController`, and everything on these two bars does. The day this
 * sensor wants a search drawer or a paid action, it gains a descriptors file
 * and the controls inside it carry `operatorKey` (D9).
 *
 * Two slots only. There is no `track` or `vessel` bar because this operator has
 * neither — the slots are optional on `SensorToolbarConfig` precisely so a
 * sensor states the bars it has rather than declaring empty ones.
 */

/**
 * Derived from the app-wide catalogue, not restated (D16) — see
 * `VR_OPERATOR_KEY` for the two rounds it took to notice the third copy.
 */
export const AISSTREAM_OPERATOR_KEY = OPERATORS.AISSTREAM;

/** AISstream: real-time SSE stream. Live badge + full transport playback. */
const AISSTREAM_CONFIG: SensorToolbarConfig = {
  operatorKey: AISSTREAM_OPERATOR_KEY,
  // `clustering` only: this operator pays for nothing and has no track replay
  // or worldwide search. Transport, labels and the type filter are not
  // capabilities — every operator has them by construction.
  //
  // Grouping was added in D10, and it is the first capability this config has
  // ever declared. What made it possible was moving the count-bubble pass out
  // of `DisplayController` (which takes Voyage Radar's map context) into
  // `shared/leaflet/cluster.layer.ts`, which takes a marker array and four
  // hooks. Declaring the capability without that would have mounted a live
  // button over a base-class no-op — exactly what `requires` exists to prevent.
  capabilities: ['clustering'],
  live: [
    { id: 'live-badge', order: 0 },
    { id: 'sep', order: 5 },
    { id: 'type-filter', order: 10 },
    // The same T button the playback bar below already carries, in the same
    // position relative to the filter. Labels are a property of what is drawn,
    // not of playback, and the live stream is the view they are read off most.
    { id: 'labels', order: 15 },
    // Order 17 on both bars, the same as Voyage Radar's: an operator switching
    // between sensors finds grouping in the same place, and it sits beside the
    // other button that changes how the fleet is DRAWN rather than which of it
    // is shown. Now a cross-file agreement rather than one visible at a glance
    // — the shared descriptor's `defaultOrder` is the canonical position.
    { id: 'clusters', order: 17 },
    { id: 'history-entry', order: 20 },
  ],
  playback: [
    { id: 'skip-start', order: 10 },
    { id: 'step-back', order: 20 },
    { id: 'play-pause', order: 30 },
    { id: 'step-forward', order: 40 },
    { id: 'skip-end', order: 50 },
    { id: 'timeline', order: 60 },
    { id: 'speed', order: 70 },
    { id: 'labels', order: 80 },
    { id: 'clusters', order: 82 },
    { id: 'type-filter', order: 85 },
    { id: 'date-range', order: 90 },
    { id: 'sep', order: 95 },
    { id: 'close', order: 100 },
  ],
};

/**
 * Contributed into the same `SENSOR_TOOLBAR_CONFIGS` multi-token Voyage Radar's
 * config uses, so the host resolves one flat list and cannot tell which file a
 * config came from. Registered in `app.module.ts`.
 */
export const AISSTREAM_TOOLBAR_CONFIG_PROVIDERS: Provider[] = [
  { provide: SENSOR_TOOLBAR_CONFIGS, useValue: AISSTREAM_CONFIG, multi: true },
];
